"use client"

import React from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { RequisitionData } from "@/lib/intershop/interfaces/requisition/requisition"

interface AccountApprovalsListClientProps {
  accountContext: AccountContext
  initialApprovals: RequisitionData[]
  myApprovals: RequisitionData[]
  status: "pending" | "rejected"
  blok?: any
}

export default function AccountApprovalsListClient(props: AccountApprovalsListClientProps) {
  const { initialApprovals, myApprovals, accountContext, status } = props
  const t = useTranslations("storefront")

  const hasToApprove = Array.isArray(initialApprovals) && initialApprovals.length > 0
  const hasMyApprovals = Array.isArray(myApprovals) && myApprovals.length > 0

  function formatShortDate(value?: string | number | Date) {
    if (!value) return "-"
    const d = new Date(value)
    if (isNaN(d.getTime())) return "-"
    const dd = d.getDate()
    const mm = d.getMonth() + 1
    const yy = d.getFullYear() % 100
    return `${dd}.${mm}.${yy}`
  }

  function formatMoney(total?: { value?: number; currency?: string } | null) {
    if (!total || total.value == null || !isFinite(Number(total.value)) || !total.currency) return "-"
    const locale = (props.accountContext?.isoLocale || "de_DE").replace("_", "-")
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: total.currency,
        currencyDisplay: "symbol",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(Number(total.value))
    } catch {
      return `${Number(total.value).toFixed(2)} ${total.currency}`
    }
  }

  function renderTable(items: RequisitionData[]) {
    return (
      <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
        <table className="min-w-full table-fixed text-left text-sm">
          <thead className="bg-gray-100 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-4 py-2 w-[18%]">{t("approvals.list.headers.requisition_no")}</th>
              <th className="px-4 py-2 w-[16%]">{t("approvals.list.headers.creation_date")}</th>
              <th className="px-4 py-2 w-[26%]">{t("approvals.list.headers.buyer")}</th>
              <th className="px-4 py-2 w-[12%]">{t("approvals.list.headers.items")}</th>
              <th className="px-4 py-2 w-[16%]">{t("approvals.list.headers.total")}</th>
              <th className="px-4 py-2 w-[12%]"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: RequisitionData) => {
              const data = item
              const requisitionId = data?.id ?? data?.requisitionNo ?? "-"
              const requisitionNo = data?.requisitionNo ?? data?.id ?? "-"
              const created = formatShortDate(data?.creationDate)
              const total =
                formatMoney(data?.totalGross)
                "-"
              const itemsCount = data?.lineItemCount ?? data?.totalProductQuantity ?? 0
              const buyerName =
                [data?.userInformation?.firstName, data?.userInformation?.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                data?.userInformation?.login ||
                [accountContext?.userData?.firstName, accountContext?.userData?.lastName].filter(Boolean).join(" ") ||
                accountContext?.userData?.login ||
                "-"

              const href = getLinkWithPrefixFromURLAndLocale(
                `/account/approvals/${requisitionId}`,
                accountContext.sbLocale
              )

              return (
                <tr key={String(requisitionId)} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono break-words">{String(requisitionNo)}</td>
                  <td className="px-4 py-2">{created}</td>
                  <td className="px-4 py-2 break-words">{buyerName}</td>
                  <td className="px-4 py-2">{itemsCount}</td>
                  <td className="px-4 py-2">{total}</td>
                  <td className="px-4 py-2 text-right">
                    <Link href={href} className="text-primary hover:underline">
                      {t("approvals.list.details_link")}
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const toApproveHeadingKey =
    status === "pending"
      ? "approvals.list.headings.to_approve.pending"
      : "approvals.list.headings.to_approve.rejected"
  const myApprovalsHeadingKey =
    status === "pending"
      ? "approvals.list.headings.my_requests.pending"
      : "approvals.list.headings.my_requests.rejected"

  if (!hasToApprove && !hasMyApprovals) {
    return (
      <div className="rounded border border-gray-200 bg-white p-4">{t("approvals.list.no_entries")}</div>
    )
  }

  return (
    <div className="space-y-6">
      {hasToApprove && (
        <section>
          <h3 className="mb-2 text-base font-semibold">{t(toApproveHeadingKey)}</h3>
          {renderTable(initialApprovals)}
        </section>
      )}

      {hasMyApprovals && (
        <section>
          <h3 className="mb-2 text-base font-semibold">{t(myApprovalsHeadingKey)}</h3>
          {renderTable(myApprovals)}
        </section>
      )}
    </div>
  )
}
